import { W, H, GLYPH, CHIBI, CHIBI_SIDE, rng } from "./paint";

/**
 * 장면 그리기 함수 모음.  scene(g, t, cue)
 *   g   : paint.js 의 painter
 *   t   : 장면이 시작된 뒤 지난 초 (12fps 로 끊김)
 *   cue : cue("결제") → 그 연출이 시작된 뒤 지난 초, 아직이면 -1
 * 원고에서 [장면 room] 으로 바꾸고, [연출 결제] 로 장면 안의 이벤트를 터뜨립니다.
 */

const step = (t, every) => Math.floor(t / every);
const ease = (k) => 1 - Math.pow(1 - Math.min(1, Math.max(0, k)), 3);

/* ---------- 공용 소품 ---------- */

const nightWindow = (g, t, x, y, w, h, seed = 3) => {
  g.rect(x - 2, y - 2, w + 4, h + 4, "e");
  g.rect(x, y, w, h, "0");
  const r = rng(seed);
  for (let i = 0; i < 14; i++) {
    const sx = x + 1 + Math.floor(r() * (w - 2));
    const sy = y + 1 + Math.floor(r() * (h * 0.45));
    if ((step(t, 0.5) + i) % 6 !== 0) g.px(sx, sy, i % 3 ? "a" : "6");
  }
  g.circle(x + w - 9, y + 7, 3, "6");
  g.circle(x + w - 8, y + 6, 2, "0");
  // 건물과 켜진 창
  let bx = x;
  const rb = rng(seed + 7);
  while (bx < x + w) {
    const bw = 5 + Math.floor(rb() * 7);
    const bh = 6 + Math.floor(rb() * (h * 0.45));
    g.rect(bx, y + h - bh, Math.min(bw, x + w - bx), bh, "2");
    for (let wy = y + h - bh + 2; wy < y + h - 1; wy += 3)
      for (let wx = bx + 1; wx < Math.min(bx + bw - 1, x + w - 1); wx += 2)
        if (rb() < 0.35 && (step(t, 3) + wx + wy) % 7 !== 0) g.px(wx, wy, "5");
    bx += bw + 1;
  }
  g.rect(x + (w >> 1), y, 1, h, "e");
  g.rect(x, y + (h >> 1) - 4, w, 1, "e");
};

const desk = (g, x, y, w) => {
  g.rect(x, y, w, 4, "d");
  g.rect(x, y + 4, w, 1, "e");
  g.rect(x + 3, y + 5, 3, 100 - y, "e");
  g.rect(x + w - 6, y + 5, 3, 100 - y, "e");
};

const laptop = (g, x, y, screen = "7") => {
  // x,y = 화면 왼쪽 위. 화면 30×18
  g.rect(x - 2, y - 2, 34, 22, "b");
  g.rect(x, y, 30, 18, screen);
  g.rect(x - 6, y + 20, 42, 2, "a");
  g.rect(x - 6, y + 22, 42, 1, "b");
};

const screenLines = (g, t, x, y, c = "8", speed = 1.2) => {
  const off = step(t, speed) % 4;
  for (let i = 0; i < 5; i++) {
    const ly = y + 3 + i * 3 - off;
    if (ly < y + 1 || ly > y + 15) continue;
    g.rect(x + 3, ly, 8 + ((i * 7) % 15), 1, c);
  }
  if (step(t, 0.5) % 2) g.rect(x + 3, y + 15, 2, 1, "6");
};

const blinking = (t, seed = 0) => (t + seed) % 3.4 < 0.17;

/** 앉아 있는 나 (옆모습). blush: 볼 빨개짐, slump: 엎드림 */
const me = (g, t, x, y, { slump = false, sway = 0, blush = false } = {}) => {
  const bob = step(t, 1.6) % 2;
  g.sprite(CHIBI_SIDE.slice(9), x, y + 9);
  const hy = y + bob + (slump ? 4 : 0);
  g.sprite(CHIBI_SIDE.slice(0, 9), x + sway, hy);
  if (slump || blinking(t)) {
    g.px(x + sway + 9, hy + 5, "c");
    g.px(x + sway + 8, hy + 6, "0");
    g.px(x + sway + 9, hy + 6, "0");
  }
  if (blush) g.rect(x + sway + 9, hy + 6, 2, 1, "9");
};

/** 서 있는 2등신. look: -1 왼쪽, 0 정면, 1 오른쪽 */
const chibi = (g, t, x, y, { hair = "0", cloth = "4", pants = "1", look = 0, seed = 0, bob = true, blush = true } = {}) => {
  const b = bob ? (step(t, 1.4) + seed) % 2 : 0;
  g.sprite(CHIBI.slice(8), x, y + 8, { map: { 4: cloth, 1: pants, c: "c" } });
  g.sprite(CHIBI.slice(0, 8), x, y + b, { map: { 0: hair, c: "c", 9: blush ? "9" : "c" } });
  // 눈
  g.px(x + 3, y + b + 4, "c");
  g.px(x + 6, y + b + 4, "c");
  if (!blinking(t, seed * 0.7)) {
    g.px(x + 3 + look, y + b + 4, "0");
    g.px(x + 6 + look, y + b + 4, "0");
  } else {
    g.px(x + 3 + look, y + b + 5, "0");
    g.px(x + 6 + look, y + b + 5, "0");
  }
};

const meStand = (g, t, x, y, look = 0) => chibi(g, t, x, y, { look });

/** 머리 위 감정 말풍선: ! ? ♥ 땀 z ♪ */
const EMOTE = {
  "!": [".#.", ".#.", ".#.", "...", ".#."],
  "?": ["###", "..#", ".##", "...", ".#."],
  heart: GLYPH.heart,
  sweat: ["..#..", ".###.", "##.##", "#####", ".###."],
  z: ["####.", "..#..", ".#...", "####.", "....."],
  note: ["..##", "..#.", "..#.", "###.", "##.."],
  star: GLYPH.sparkle,
};
const EMOTE_COLOR = { "!": "9", "?": "3", heart: "9", sweat: "7", z: "a", note: "4", star: "5" };
const emote = (g, t, x, y, kind) => {
  const pop = step(t, 0.5) % 2;
  const yy = y - pop;
  g.box(x, yy, 9, 8, "6", "6");
  g.px(x + 2, yy + 8, "6");
  g.px(x + 1, yy + 9, "6");
  const glyph = EMOTE[kind] || EMOTE["!"];
  g.sprite(glyph, x + Math.floor((9 - glyph[0].length) / 2), yy + 1 + (glyph.length < 5 ? 1 : 0), { tint: EMOTE_COLOR[kind] || "9" });
};

/** 웅크려 자는 고양이 */
const cat = (g, t, x, y, c = "a") => {
  g.rect(x, y + 2, 11, 4, c);
  g.rect(x + 1, y + 1, 9, 1, c);
  g.rect(x + 8, y, 4, 4, c);
  g.px(x + 8, y - 1, c);
  g.px(x + 11, y - 1, c);
  g.px(x + 10, y + 2, "0");
  g.rect(x - 2 - (step(t, 0.6) % 2), y + 5, 3, 1, c);
  const zp = (t * 0.5) % 1;
  if (zp < 0.8) g.sprite(EMOTE.z.slice(0, 4).map((r) => r.slice(0, 4)), x + 12 + zp * 4, y - 6 - zp * 8, { tint: "a" });
};

const purchasePopup = (g, k, x, y) => {
  // k = 연출 뒤 지난 초. 3초 뒤 사라짐
  if (k < 0 || k > 3) return;
  const show = Math.min(1, k / 0.25);
  const w = Math.round(24 * show), h = Math.round(14 * show);
  g.box(x + 12 - (w >> 1), y + 7 - (h >> 1), w, h, "6", "4");
  if (show >= 1) {
    g.sprite(GLYPH.check, x + 3, y + 5, { tint: "4" });
    g.rect(x + 10, y + 5, 10, 1, "a");
    g.rect(x + 10, y + 8, 7, 1, "a");
  }
  // 동전
  if (k < 1.6)
    for (let i = 0; i < 4; i++) {
      const p = (k * 1.4 + i * 0.22) % 1;
      g.rect(x + 2 + i * 6, y - p * 16, 2, 2, "5");
      g.px(x + 2 + i * 6, y - p * 16, "6");
    }
};

const sparklesAround = (g, t, cx, cy, r, c = "5", n = 3) => {
  for (let i = 0; i < n; i++) {
    const a = t * 2.2 + (i * Math.PI * 2) / n;
    g.sprite(GLYPH.sparkle, cx + Math.cos(a) * r - 1, cy + Math.sin(a) * r * 0.5 - 1, { tint: c });
  }
};

const floatingHearts = (g, t, x0, y0, n, c = "9", seed = 1) => {
  const r = rng(seed);
  for (let i = 0; i < n; i++) {
    const ox = Math.floor(r() * 60);
    const sp = 0.25 + r() * 0.25;
    const p = (t * sp + r()) % 1;
    const y = y0 - p * 40;
    if (p > 0.85 && step(t, 0.17) % 2) continue;
    g.sprite(GLYPH.heart, x0 + ox + Math.round(Math.sin((t + i) * 2) * 2), y, { tint: c });
  }
};

/* ---------- 장면 ---------- */

/** 밤, 내 방 책상. 취기 · 앱 · 결제 */
const room = (g, t, cue) => {
  g.rect(0, 0, W, H, "1");
  nightWindow(g, t, 100, 8, 48, 34, 3);
  g.rect(0, 78, W, 22, "2");
  g.rect(0, 78, W, 1, "3");
  // 스탠드
  g.glow(22, 50, 20, "5", 0.4);
  g.rect(18, 61, 8, 3, "b");
  g.rect(21, 48, 2, 13, "b");
  for (let j = 0; j < 6; j++) g.rect(19 - j, 41 + j, 6 + j * 2, 1, "5");
  desk(g, 12, 64, 108);
  // 노트북 + 빛
  g.glow(75, 50, 24, "3", 0.4);
  laptop(g, 60, 41);
  const app = cue("앱");
  if (app >= 0) {
    g.rect(60, 41, 30, 18, "0");
    const cx = 75, cy = 50;
    for (let j = -7; j <= 7; j++)
      for (let i = -7; i <= 7; i++) {
        if (i * i + j * j > 50) continue;
        const land = (Math.floor((i + t * 6) / 3) + Math.floor(j / 2) * 2) % 3 === 0;
        g.px(cx + i, cy + j, land ? "f" : "8");
      }
    if (step(app, 0.3) % 2 && app < 2) g.text("+", cx + 9, cy - 8, "5");
  } else {
    screenLines(g, t, 60, 41);
  }
  purchasePopup(g, cue("결제"), 63, 44);
  // 술잔과 기포
  const drunk = cue("취기");
  g.rect(100, 55, 1, 9, "6");
  g.rect(107, 55, 1, 9, "6");
  g.rect(100, 63, 8, 1, "6");
  g.rect(101, 58, 6, 5, "4");
  const n = drunk >= 0 ? 5 : 2;
  for (let i = 0; i < n; i++) {
    const p = (t * (drunk >= 0 ? 1.4 : 0.6) + i / n) % 1;
    g.px(102 + ((i * 3) % 5), 62 - p * (drunk >= 0 ? 14 : 5), "6");
  }
  // 의자와 나
  cat(g, t, 124, 86, "a");
  g.rect(38, 62, 18, 16, "e");
  me(g, t, 40, 50, { sway: drunk >= 0 ? Math.round(Math.sin(t * 2.4)) : 0, blush: drunk >= 0 });
  if (drunk >= 0) {
    sparklesAround(g, t, 47, 50, 11, "5", 3);
    g.tint("9", 0.08 + 0.04 * (step(t, 0.5) % 2));
  }
  const pay = cue("결제"), app2 = cue("앱");
  if (pay >= 0 && pay < 3) emote(g, t, 47, 36, "note");
  else if (app2 >= 0 && app2 < 3) emote(g, t, 47, 36, "!");
  else if (drunk >= 0) emote(g, t, 47, 36, "heart");
};

/** 모임. 스토리 태그와 왕관 */
const party = (g, t, cue) => {
  g.rect(0, 0, W, H, "1");
  g.rect(0, 74, W, 26, "2");
  // 전구 줄
  for (let x = 0; x < W; x++) {
    const y = 10 + Math.round(Math.sin((x / W) * Math.PI) * 8);
    g.px(x, y, "b");
    if (x % 12 === 4) {
      const c = ["4", "9", "5", "7", "f"][(x / 12 + step(t, 0.5)) % 5 | 0];
      g.rect(x - 1, y + 1, 3, 3, c);
      g.glow(x, y + 2, 6, c, 0.35);
    }
  }
  // 사람들
  const crowd = [[12, "e", "7"], [28, "5", "9"], [46, "d", "f"], [62, "0", "a"]];
  crowd.forEach(([x, hair, cloth], i) => {
    chibi(g, t, x, 60, { hair, cloth, seed: i, look: i < 2 ? 1 : -1 });
    g.rect(x + 9, 70 + ((step(t, 0.9) + i) % 2), 2, 3, "5");
  });
  meStand(g, t, 82, 60, -1);
  const show = cue("으스댐"), tagged = cue("태그");
  if (show >= 0) {
    g.sprite(GLYPH.crown, 83, 53 - (show < 0.4 ? 3 - show * 7 : 0), { tint: "5" });
    sparklesAround(g, t, 87, 55, 9, "6", 2);
  } else if (tagged >= 0) emote(g, t, 84, 48, "note");
  // 앞쪽 휴대폰 (스토리 화면)
  g.box(104, 16, 48, 86, "0", "a");
  g.rect(107, 20, 42, 80, "3");
  g.rect(107, 70, 42, 30, "2");
  for (let i = 0; i < 3; i++) g.rect(109 + i * 13, 22, 12, 1, i < 2 ? "6" : "b");
  [[110, 50, "e", "7"], [122, 46, "5", "9"], [134, 50, "d", "f"]].forEach(([x, y, hair, cloth], i) =>
    chibi(g, t, x, y, { hair, cloth, seed: i + 2, bob: false }));
  chibi(g, t, 122, 64, { cloth: "4", bob: false });
  const tag = cue("태그");
  if (tag >= 0)
    [[110, 32], [130, 38], [112, 82], [134, 84]].forEach(([x, y], i) => {
      const k = tag - i * 0.35;
      if (k < 0) return;
      const pop = k < 0.12 ? 1 : 0;
      g.box(x - pop, y - pop, 16 + pop * 2, 9 + pop * 2, "6", "6");
      g.sprite(GLYPH.at, x + 2, y + 2, { tint: "4" });
      g.rect(x + 8, y + 4, 6, 1, "a");
    });
};

/** 아침, 맨 정신. 압도 · 미루기 */
const morning = (g, t, cue) => {
  g.rect(0, 0, W, H, "g");
  // 창: 흐린 하늘과 구름
  g.rect(98, 6, 52, 38, "i");
  g.rect(100, 8, 48, 34, "h");
  const cx = 100 + ((t * 3) % 60) - 12;
  g.rect(cx, 16, 14, 3, "6");
  g.rect(cx + 3, 14, 7, 2, "6");
  g.rect(124, 8, 1, 34, "i");
  g.rect(100, 24, 48, 1, "i");
  g.rect(0, 78, W, 22, "i");
  // 달력
  const k = cue("미루기");
  const flips = k >= 0 ? Math.min(14, step(k, 0.16)) : 0;
  g.rect(24, 12, 18, 20, "6");
  g.rect(24, 12, 18, 4, "9");
  g.text(String(3 + flips).padStart(2, "0"), 27, 20, "0");
  if (k >= 0 && k < 2.4 && step(k, 0.16) % 2) g.rect(24, 16, 18, 3, "g");
  desk(g, 12, 64, 108);
  laptop(g, 60, 41, "i");
  for (let i = 0; i < 6; i++) g.rect(61 + i * 5, 42, 4, 2, i === 5 ? "9" : "a");
  // 쌓인 택배 상자 (결제한 강의들)
  [[124, 66, 22, 12], [128, 56, 16, 10], [130, 48, 12, 8]].forEach(([x, y, w, h]) => {
    g.rect(x, y, w, h, "d");
    g.rect(x + (w >> 1) - 1, y, 2, h, "5");
    g.rect(x, y, w, 1, "e");
  });
  // 서류 더미
  const over = cue("압도");
  const pile = over >= 0 ? Math.min(12, 2 + step(over, 0.09)) : 2;
  for (let i = 0; i < pile; i++) g.rect(94 + ((i * 3) % 4) - 1, 62 - i * 2, 14, 2, i % 2 ? "6" : "h");
  if (over >= 0) {
    if (over > 1.2)
      for (let i = 0; i < 3; i++) {
        const p = ((over - 1.2) * 0.6 + i * 0.3) % 1;
        g.rect(96 + i * 8 + Math.sin(p * 6) * 3, 30 + p * 40, 5, 3, "6");
      }
    if (step(over, 0.3) % 2) g.text("!", 113, 34, "9");
  }
  g.rect(38, 62, 18, 16, "e");
  me(g, t, 40, 50, { slump: true });
  if (over >= 0 && over < 3) emote(g, t, 47, 38, "sweat");
  else if (k >= 0) emote(g, t, 47, 38, "z");
};

/** 술장. 유혹하는 주제들, 새로 도착한 '카드뉴스' */
const shelf = (g, t, cue) => {
  g.rect(0, 0, W, H, "l");
  for (let y = 4; y < H; y += 9) g.rect(0, y, W, 1, "e");
  [36, 70].forEach((y) => {
    g.rect(8, y, 144, 3, "d");
    g.rect(8, y + 3, 144, 1, "e");
  });
  const labels = [GLYPH.globe, GLYPH.at, GLYPH.camera, GLYPH.mic, GLYPH.drop, GLYPH.star];
  const colors = ["7", "9", "f", "a", "8", "5"];
  const sweep = step(t, 0.7) % labels.length;
  const card = cue("카드뉴스");
  labels.forEach((lab, i) => {
    const x = 16 + i * 22, y = 14;
    const lit = card < 0 && i === sweep;
    if (lit) g.glow(x + 5, y + 12, 14, "5", 0.5);
    g.rect(x + 3, y, 4, 5, "b");
    g.rect(x, y + 5, 10, 17, colors[i]);
    g.rect(x + 2, y + 9, 6, 7, "6");
    g.sprite(lab, x + 3 - (lab[0].length > 4 ? 1 : 0), y + 10, { tint: colors[i] });
    g.ctx.globalAlpha = card >= 0 ? 0.6 : lit ? 0 : 0.3;
    g.rect(x, y, 10, 22, "0");
    g.ctx.globalAlpha = 1;
  });
  // 아래 칸: 빈 병들
  for (let i = 0; i < 7; i++) {
    g.rect(14 + i * 19 + 2, 49, 3, 3, "e");
    g.rect(14 + i * 19, 52, 7, 18, "e");
  }
  if (card >= 0) {
    const drop = ease(card / 0.5);
    const y = -24 + drop * (48 + (card > 0.5 && card < 0.65 ? -2 : 0));
    const x = 70;
    g.glow(x + 7, 60, 26, "4", 0.55 + 0.15 * (step(t, 0.25) % 2));
    g.rect(x + 4, y + 22, 6, 6, "b");
    g.rect(x, y + 28, 14, 20, "4");
    g.rect(x + 2, y + 32, 10, 9, "6");
    g.sprite(GLYPH.card, x + 5, y + 34, { tint: "4" });
    if (card > 0.6) sparklesAround(g, t, x + 7, y + 34, 13, "6", 4);
  }
  // 앞쪽: 술병을 올려다보는 나
  meStand(g, t, 22, 80, 1);
  if (card >= 0) emote(g, t, 25, 68, "heart");
};

/** 무대 위 '반쯤 연예인' */
const stage = (g, t) => {
  g.rect(0, 0, W, H, "0");
  for (let x = 0; x < 34; x++) g.rect(x, 0, 1, 74, x % 6 < 3 ? "j" : "9");
  for (let x = 126; x < W; x++) g.rect(x, 0, 1, 74, x % 6 < 3 ? "j" : "9");
  g.rect(0, 0, W, 6, "j");
  // 조명
  g.ctx.globalAlpha = 0.16 + 0.03 * (step(t, 0.4) % 2);
  for (let y = 6; y < 74; y++) {
    const half = Math.round(4 + (y - 6) * 0.42);
    g.rect(80 - half, y, half * 2, 1, "5");
  }
  g.ctx.globalAlpha = 1;
  g.ctx.globalAlpha = 0.25;
  g.rect(52, 70, 56, 6, "5");
  g.ctx.globalAlpha = 1;
  g.rect(20, 70, 120, 6, "e");
  g.rect(20, 70, 120, 1, "d");
  const bob = step(t, 0.6) % 2;
  chibi(g, t, 75, 56 - bob, { hair: "5", cloth: "9", pants: "6", bob: false });
  sparklesAround(g, t, 80, 62, 15, "5", 4);
  // 관객과 하트
  for (let i = 0; i < 11; i++) g.circle(8 + i * 15, 92 + (i % 2), 6, "1");
  chibi(g, t, 33, 84, { look: 1, bob: false });
  emote(g, t, 36, 72, "star");
  floatingHearts(g, t, 50, 86, 6, "9", 4);
};

/** 체크리스트 창 → 닫기 */
const browser = (g, t, cue) => {
  g.rect(0, 0, W, H, "2");
  g.rect(0, 92, W, 8, "1");
  [["4", 6], ["7", 20], ["f", 34]].forEach(([c, x]) => g.rect(x, 94, 8, 4, c));
  const close = cue("창닫기");
  const gone = close >= 0 ? Math.min(1, Math.max(0, (close - 0.85) / 0.6)) : 0;
  if (gone < 1) {
    g.rect(14, 8, 132, 82, "6");
    g.rect(14, 8, 132, 9, "g");
    ["9", "5", "f"].forEach((c, i) => g.rect(18 + i * 5, 11, 3, 3, c));
    const hot = close >= 0.7 && close < 0.85;
    g.rect(136, 10, 7, 5, hot ? "9" : "a");
    g.px(138, 11, "6"); g.px(140, 11, "6"); g.px(139, 12, "6"); g.px(138, 13, "6"); g.px(140, 13, "6");
    g.rect(24, 24, 70, 4, "1");
    [36, 58].forEach((y, i) => {
      g.circle(29, y + 4, 5, "4");
      g.sprite(["###", "..#", ".##", "...", ".#."], 28, y + 2, { tint: "6" });
      g.rect(40, y + 1, 80 - i * 14, 2, "a");
      g.rect(40, y + 6, 56 + i * 10, 2, "g");
      g.rect(40, y + 11, 10, 6, "6");
      g.box(40, y + 11, 10, 6, "6", "a");
    });
    if (gone > 0) g.dither(14, 8, 132, 82, "2", gone * 1.05);
  }
  // 커서
  let cx = 92 + Math.round(Math.sin(t * 0.8) * 3), cy = 72;
  if (close >= 0) {
    const k = ease(Math.min(1, close / 0.7));
    cx = 92 + (139 - 92) * k;
    cy = 72 + (12 - 72) * k;
  }
  g.sprite(GLYPH.arrow, cx + 1, cy + 1, { tint: "0" });
  g.sprite(GLYPH.arrow, cx, cy, { tint: "6" });
};

/** 숨고 싶은 방 ↔ 드러나는 무대 */
const split = (g, t) => {
  const side = step(t, 2.4) % 2; // 0: 왼쪽(숨기) 보기, 1: 오른쪽(드러내기)
  // 왼쪽 방
  g.rect(0, 0, 80, H, "k");
  g.rect(0, 74, 80, 26, "e");
  g.glow(20, 40, 22, "5", 0.32);
  g.rect(16, 30, 9, 5, "5");
  g.rect(20, 35, 1, 20, "b");
  g.rect(8, 56, 26, 16, "j");
  g.rect(8, 52, 6, 20, "j");
  g.rect(14, 54, 12, 3, "6");
  g.rect(14, 54, 6, 1, "a");
  cat(g, t, 44, 68, "0");
  // 오른쪽 무대
  g.rect(80, 0, 80, H, "0");
  g.ctx.globalAlpha = 0.16;
  for (let y = 0; y < 74; y++) {
    const half = Math.round(3 + y * 0.35);
    g.rect(120 - half, y, half * 2, 1, "5");
  }
  g.ctx.globalAlpha = 1;
  g.rect(80, 74, 80, 4, "e");
  for (let i = 0; i < 6; i++) g.circle(86 + i * 14, 94, 6, "1");
  floatingHearts(g, t, 90, 88, 4, "9", 9);
  // 어두워지는 쪽
  g.ctx.globalAlpha = 0.55;
  g.rect(side ? 0 : 80, 0, 80, H, "0");
  g.ctx.globalAlpha = 1;
  for (let y = 0; y < H; y += 2) g.px(80, y, "3");
  // 가운데 선 위의 나
  meStand(g, t, 75, 60, side === 0 ? -1 : 1);
};

/** 검색 결과 속 '촉촉한마케터' */
const search = (g, t, cue) => {
  g.rect(0, 0, W, H, "1");
  g.rect(14, 8, 132, 84, "6");
  g.box(24, 14, 112, 11, "6", "a");
  g.circle(31, 19, 3, "a");
  g.circle(31, 19, 2, "6");
  g.px(34, 22, "a"); g.px(35, 23, "a");
  const typed = Math.min(14, step(t, 0.12));
  for (let i = 0; i < typed; i++) g.rect(38 + i * 6, 18, 4, 3, "1");
  if (step(t, 0.4) % 2) g.rect(38 + typed * 6, 17, 1, 5, "1");
  const hit = cue("촉마");
  [32, 50, 68].forEach((y, i) => {
    const on = hit >= 0 && i === 1;
    g.box(24, y, 112, 15, on ? "6" : "6", on ? "4" : "g");
    if (on) {
      g.sprite(GLYPH.drop, 29, y + 4, { tint: "4" });
      g.rect(38, y + 4, 50, 2, "4");
      g.rect(38, y + 9, 70, 1, "a");
      if (hit < 1.6) sparklesAround(g, t, 31, y + 7, 9, "4", 3);
    } else {
      g.rect(29, y + 4, 6, 6, "g");
      g.rect(38, y + 4, 44, 2, "a");
      g.rect(38, y + 9, 64, 1, "g");
    }
  });
};

/** 카페, 칼럼 '취기가 가시기 전에' */
const cafe = (g, t, cue) => {
  g.rect(0, 0, W, H, "k");
  // 창밖 거리
  g.rect(8, 6, 76, 46, "e");
  g.rect(10, 8, 72, 42, "h");
  g.rect(10, 38, 72, 12, "i");
  for (let i = 0; i < 3; i++) {
    const x = 10 + ((t * (6 + i * 3) + i * 30) % 80) - 4;
    if (x > 10 && x < 80) {
      g.rect(x, 33, 2, 2, "1");
      g.rect(x, 35, 2, 4, i === 1 ? "4" : "3");
    }
  }
  g.rect(46, 8, 1, 42, "e");
  // 화분
  g.rect(140, 60, 10, 10, "d");
  const sway = step(t, 0.8) % 2;
  [[139, 50], [144, 46], [148, 52], [142, 55]].forEach(([x, y], i) => g.rect(x + (i % 2 ? sway : -sway), y, 3, 6, "f"));
  desk(g, 10, 70, 128);
  // 커피와 김
  g.rect(108, 61, 9, 9, "6");
  g.rect(109, 62, 7, 2, "e");
  g.rect(117, 63, 2, 4, "6");
  for (let i = 0; i < 3; i++) {
    const p = (t * 0.7 + i / 3) % 1;
    g.px(110 + i * 2 + Math.round(Math.sin(p * 9 + i) * 1.5), 60 - p * 12, "6");
  }
  // 노트북: 칼럼 화면
  g.rect(54, 46, 34, 22, "b");
  g.rect(56, 48, 30, 18, "6");
  g.rect(56, 48, 30, 3, "4");
  const off = step(t, 1.4) % 3;
  for (let i = 0; i < 4; i++) g.rect(59, 53 + i * 3 - off, 18 + ((i * 5) % 7), 1, "a");
  g.rect(50, 68, 42, 2, "a");
  // 칼럼 속 연출
  const novel = cue("소설");
  if (novel >= 0) {
    for (let i = 0; i < 5; i++) {
      const p = (novel * 0.6 + i * 0.2) % 1;
      g.sprite(GLYPH.book, 60 + i * 6 + Math.sin(p * 5) * 3, 44 - p * 34, { map: { 6: ["5", "9", "7", "f", "6"][i] }, tint: "e" });
    }
    const n = Math.min(253, Math.floor(253 * ease(novel / 1.5)));
    g.box(90, 8, 20, 9, "0", "4");
    g.text(String(n).padStart(3, "0"), 92, 10, "5");
  }
  purchasePopup(g, cue("스크리브너"), 59, 50);
  const glass = cue("모래시계");
  if (glass >= 0) {
    const k = Math.min(1, glass / 2);
    const x = 124, y = 50;
    g.rect(x, y, 9, 1, "d");
    g.rect(x, y + 19, 9, 1, "d");
    for (let j = 0; j < 9; j++) {
      const half = Math.max(1, 4 - j * 0.45);
      g.rect(x + 4.5 - half, y + 1 + j, half * 2, 1, j / 9 > k ? "5" : "h");
      g.rect(x + 4.5 - half, y + 18 - j, half * 2, 1, j / 9 < k ? "5" : "h");
    }
    if (k < 1) g.rect(x + 4, y + 9, 1, 9, "5");
  }
  const small = cue("작은일");
  if (small >= 0) {
    // 등장인물 10명
    for (let i = 0; i < 10; i++) {
      if (small < i * 0.12) break;
      const c = ["9", "4", "7", "f", "5"][i % 5];
      const hop = step(small, 0.12) === i ? 1 : 0;
      g.sprite([".#.", "###", "#.#"], 50 + i * 4, 38 - hop, { tint: c });
    }
    if (small > 1.4) g.sprite(GLYPH.check, 92, 37, { tint: "4" });
  }
  const react = cue("반응");
  if (react >= 0) {
    floatingHearts(g, react, 50, 46, 4, "9", 3);
    if (step(react, 0.5) % 2) g.sprite(GLYPH.eye, 92, 38, { tint: "6" });
  }
  g.rect(28, 66, 18, 14, "e");
  me(g, t, 30, 56, { blush: novel >= 0 && novel < 3 });
  const sand = cue("모래시계");
  if (novel >= 0 && novel < 2.5) emote(g, t, 37, 41, "!");
  else if (sand >= 0 && sand < 3 && cue("물음표") < 0) emote(g, t, 37, 41, "sweat");
  const huh = cue("물음표");
  if (huh >= 0) {
    const by = 41 - (step(huh, 0.4) % 2);
    g.box(32, by, 11, 10, "6", "6");
    g.px(36, by + 10, "6");
    g.text("?", 36, by + 2, "0");
  }
};

/** 장면이 없을 때 */
const blank = (g) => g.rect(0, 0, W, H, "0");

export const SCENES = { room, party, morning, shelf, stage, browser, split, search, cafe, blank };
